import Box from "@mui/material/Box";
import type React from "react";
import { useImperativeHandle, useRef } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { useCheckEta } from "@/hooks/dispatch/useCheckEta";
import { useMembershipCurrentRoutes } from "@/hooks/dispatch/useCurrentRoutes";
import { useMembershipHosClocks } from "@/hooks/dispatch/useHosClocks";
import type { DriverRow, TagDefinition } from "@/types/driver";
import { type DispatchPanelHandle, scrollByPage } from "../panelScroll";
import HosBlock from "./HosBlock";
import InfoBlock from "./InfoBlock";
import RouteBlock from "./RouteBlock";
import StatusBlock from "./StatusBlock";

/**
 * The Context tab as four sections, top to bottom: Info, HOS, Route and Status.
 *
 * Info answers who the driver is and what they run, then when and where they free up; HOS the hours that bound what
 * they can take next; Route the route with every stop; Status the driver's device. Each section opens on the same
 * header band, and the gap between sections is wider than any gap inside one, so a dispatcher scanning down the
 * panel finds the section before reading it.
 *
 * The route sections read the per-driver route poll while the panel is open, so they move ahead of the table's
 * fleet-wide poll, and fall back to the row's own summary until that first answer lands. The HOS section reads its
 * own poll, so the route sections never wait on it.
 *
 * The panel is its own scroller, and the page keys page it through `handleRef`. Nothing keys it per driver, so when the
 * popover follows the selection to another driver the scroll position stays where the dispatcher left it.
 */
export default function ContextPanel({
  driver,
  tags,
  workspaceId,
  now,
  handleRef,
}: {
  driver: DriverRow;
  tags: readonly TagDefinition[];
  workspaceId: string;
  /** The table's clock, so the PTA's "in 2 hr" ticks with the rows. */
  now: number;
  /** Receives the panel's {@link DispatchPanelHandle}, which pages its sections. */
  handleRef?: React.Ref<DispatchPanelHandle>;
}) {
  const live = useMembershipCurrentRoutes(workspaceId, driver.workspaceMembershipId);
  const route = live === undefined ? (driver.route ?? null) : live;
  const hos = useMembershipHosClocks(workspaceId, driver.workspaceMembershipId);
  const checkEta = useCheckEta(workspaceId, driver.feedId);
  const scrollerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    handleRef,
    () => ({ scrollPage: (direction) => scrollByPage(scrollerRef.current, direction) }),
    [],
  );
  return (
    <Box
      ref={scrollerRef}
      data-sb={TestIds.dispatch.contextPanel}
      sx={{ display: "flex", flexDirection: "column", gap: 2, m: 2, overflowY: "auto" }}
    >
      <InfoBlock driver={driver} tags={tags} route={route} now={now} />
      <HosBlock hos={hos} driver={driver} />
      <RouteBlock route={route} onCheckEta={checkEta} />
      <StatusBlock driver={driver} />
    </Box>
  );
}
